// Pure filter logic for the Activity ledger — kept out of the screen so it can
// be unit-tested under plain node (tests/investment-v2/activity-filters.test.mjs).
//
// Every filter ANDs. Fields come from activityRows.js rows (exposed as
// data.allEvents by useInvestmentData, and as the admin ledger by
// useAdminActivity): srcKind / dstKind (account-kind slugs), amount (moved),
// eventDate, value, noValue, movementStatus ('pending' on an unconfirmed
// contribution), custody ('investor' | 'pool' | null), enteredByRole
// ('admin' | 'executor' | null), member (account owner) and household
// (the enrolled parent member).

export const EMPTY_FILTERS = {
  q: '',
  who: '',         // member id — matches the row's owner OR its household (admin view)
  src: '',         // account-kind slug
  dst: '',         // account-kind slug
  minAmt: '',      // amount moved ≥
  maxAmt: '',      // amount moved ≤
  from: '',        // yyyy-mm-dd
  to: '',          // yyyy-mm-dd
  value: 'all',    // all | generated | none | pending
  holder: 'all',   // all | investor | pool
  loggedBy: 'all', // all | admin | executor | agent
};

export const isActive = (f) => Object.keys(EMPTY_FILTERS).some((k) => f[k] !== EMPTY_FILTERS[k]);
export const activeCount = (f) => Object.keys(EMPTY_FILTERS).filter((k) => f[k] !== EMPTY_FILTERS[k]).length;

// The yyyy-mm-dd prefix of an event's date, whatever timestamp shape it came in.
const dayOf = (iso) => String(iso || '').slice(0, 10);

export function applyFilters(events, f, byId = {}) {
  const q = String(f.q || '').trim().toLowerCase();
  const minAmt = f.minAmt === '' || f.minAmt == null ? null : Number(f.minAmt);
  const maxAmt = f.maxAmt === '' || f.maxAmt == null ? null : Number(f.maxAmt);
  return events.filter((e) => {
    if (f.who && e.member !== f.who && e.household !== f.who) return false;
    if (f.src && e.srcKind !== f.src) return false;
    if (f.dst && e.dstKind !== f.dst) return false;
    const amt = Math.abs(Number(e.amount || 0));
    if (minAmt !== null && !isNaN(minAmt) && amt < minAmt) return false;
    if (maxAmt !== null && !isNaN(maxAmt) && amt > maxAmt) return false;
    const day = dayOf(e.eventDate);
    if (f.from && day < f.from) return false;
    if (f.to && day > f.to) return false;
    const pending = e.movementStatus === 'pending';
    const generated = !e.noValue && Number(e.value || 0) !== 0;
    if (f.value === 'generated' && !generated) return false;
    if (f.value === 'none' && (generated || pending)) return false;
    if (f.value === 'pending' && !pending) return false;
    if (f.holder !== 'all' && e.custody !== f.holder) return false;
    if (f.loggedBy === 'admin' && e.enteredByRole !== 'admin') return false;
    if (f.loggedBy === 'executor' && e.enteredByRole !== 'executor') return false;
    if (f.loggedBy === 'agent' && e.enteredByRole) return false;
    if (q) {
      const m = byId[e.member];
      const h = e.household && e.household !== e.member ? byId[e.household] : null;
      const hay = [e.flow, e.srcLabel, e.dstLabel, e.date, e.amount, e.value, m?.name, h?.name, e.custody, e.enteredByRole]
        .join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

// Distinct { slug, label } for one side of the flow, alphabetical by label.
export function accountOptions(events, kindKey, labelKey) {
  const seen = new Map();
  events.forEach((e) => { if (e[kindKey] && !seen.has(e[kindKey])) seen.set(e[kindKey], e[labelKey] || e[kindKey]); });
  return [...seen.entries()].map(([slug, label]) => ({ slug, label })).sort((a, b) => a.label.localeCompare(b.label));
}

// Who-options for the admin ledger: every household that has rows, each
// followed by its sub-accounts that have rows of their own. Rows carry
// { id, label, indent } so the select can show the household tree.
export function whoOptions(events, byId = {}) {
  const households = new Map(); // householdId → Set(memberIds with rows)
  events.forEach((e) => {
    const h = e.household || e.member;
    if (!h) return;
    if (!households.has(h)) households.set(h, new Set());
    if (e.member && e.member !== h) households.get(h).add(e.member);
  });
  const name = (id) => byId[id]?.name || id;
  return [...households.entries()]
    .sort((a, b) => name(a[0]).localeCompare(name(b[0])))
    .flatMap(([h, subs]) => [
      { id: h, label: name(h), indent: false },
      ...[...subs].sort((a, b) => name(a).localeCompare(name(b))).map((s) => ({ id: s, label: name(s), indent: true })),
    ]);
}
